import type { ReactNode } from 'react';
import { useI18n } from '@apis/i18n';
import { Button, Card, Logo } from '@apis/ui';
import type { MeResponse } from '@apis/contracts';
import type { MeBranch } from '../lib/access';
import { useSignOut, useSwitchTenant } from '../lib/session';
import { Link, useNavigate } from '@tanstack/react-router';
import { LanguageSwitch } from './language-switch';

/** Bare centred page (pickers, access denied, fatal errors). */
export function CenteredLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-[var(--bg)] px-4 py-6">
      <div className="mx-auto flex w-full max-w-md items-center justify-between">
        <Logo />
        <LanguageSwitch />
      </div>
      <main id="main" className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
        {children}
      </main>
    </div>
  );
}

function SignOutButton() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const signOut = useSignOut(() => void navigate({ to: '/login', replace: true }));
  return (
    <Button className="mt-6 w-full" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
      {signOut.isPending ? t('common.signingOut') : t('common.signOut')}
    </Button>
  );
}

export function AccessDenied({ app, reason }: { app: string; reason: 'permission' | 'tenant' }) {
  const { t } = useI18n();
  return (
    <CenteredLayout>
      <Card className="p-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {reason === 'tenant' ? t('access.noTenant.title') : t('access.denied.title', { app })}
        </h1>
        <p className="mt-2 text-sm text-[var(--ink-soft)]">
          {reason === 'tenant' ? t('access.noTenant.description') : t('access.denied.description')}
        </p>
        {reason === 'permission' ? (
          <Link
            to="/"
            className="mt-6 block text-sm font-medium text-[var(--accent-ink)] hover:underline"
          >
            {t('common.allInterfaces')}
          </Link>
        ) : null}
        <SignOutButton />
      </Card>
    </CenteredLayout>
  );
}

export function BranchPicker({
  app,
  branches,
  onSelect,
}: {
  app: string;
  branches: readonly MeBranch[];
  onSelect: (id: string) => void;
}) {
  const { t } = useI18n();
  return (
    <CenteredLayout>
      <Card className="p-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('branch.picker.title')}</h1>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">
          {t('branch.picker.description', { app })}
        </p>
        <ul className="mt-6 divide-y divide-[var(--line)]">
          {branches.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => onSelect(b.id)}
                className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-[var(--surface-muted)]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{b.name}</span>
                  <span className="block text-xs text-[var(--ink-faint)]">
                    {b.code} · {b.time_zone}
                  </span>
                </span>
                <span aria-hidden className="text-[var(--ink-faint)]">
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </CenteredLayout>
  );
}

export function TenantPicker({ me }: { me: MeResponse }) {
  const { t } = useI18n();
  const switchTenant = useSwitchTenant();
  return (
    <CenteredLayout>
      <Card className="p-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('tenant.picker.title')}</h1>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">{t('tenant.picker.description')}</p>
        <ul className="mt-6 divide-y divide-[var(--line)]">
          {me.memberships.map((m) => (
            <li key={m.tenant_id}>
              <button
                type="button"
                disabled={switchTenant.isPending}
                onClick={() => switchTenant.mutate(m.tenant_id)}
                className="flex w-full items-center justify-between rounded-lg px-2 py-3 text-left text-sm font-medium transition-colors hover:bg-[var(--surface-muted)] disabled:opacity-60"
              >
                {m.tenant_name}
                <span aria-hidden className="text-[var(--ink-faint)]">
                  {switchTenant.isPending && switchTenant.variables === m.tenant_id
                    ? t('tenant.switching')
                    : '→'}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <SignOutButton />
      </Card>
    </CenteredLayout>
  );
}
